import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api, imgUrl, formatApiError } from "../lib/api";
import { SEO } from "../components/SEO";
import { NotFound } from "../components/site/NotFound";
import { FieldError } from "../components/site/FieldError";
import { validators, runValidators } from "../lib/validation";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" };

export function CheckoutPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(undefined);
  const [form, setForm] = useState(() => {
    try {
      return { ...EMPTY, ...JSON.parse(sessionStorage.getItem("bm_checkout_form") || "{}") };
    } catch {
      return EMPTY;
    }
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlan(r.plans.find((p) => p.id === planId) || null)).catch(() => setPlan(null));
  }, [planId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fields = () => ({
    name: [form.name, [validators.minLength(2, "Name")]],
    email: [form.email, [validators.email]],
    phone: [form.phone, [validators.indianPhone]],
    address: [form.address, [validators.minLength(5, "Address")]],
    city: [form.city, [validators.required]],
    state: [form.state, [validators.required]],
    pincode: [form.pincode, [validators.pincode]],
  });
  const blur = (k) => {
    const [v, fns] = fields()[k];
    setErrors((e) => ({ ...e, [k]: fns.map((f) => f(v)).find(Boolean) || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const { errors: errs, hasError } = runValidators(fields());
    setErrors(errs);
    if (hasError) return;
    setLoading(true);
    const clean = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
    clean.email = clean.email.toLowerCase();
    try {
      // Re-validate plan validity & current price server-side before creating the order
      const check = await api.get(`/plans/${planId}/check`);
      if (!check.active) {
        toast.error("This plan is no longer available. Please pick another plan.");
        setLoading(false);
        return;
      }
      if (check.price !== plan.price) {
        setPlan({ ...plan, price: check.price, original_price: check.original_price });
        toast.info(`The plan price was updated to ₹${check.price.toLocaleString("en-IN")}. Please review and confirm again.`);
        setLoading(false);
        return;
      }
      sessionStorage.setItem("bm_checkout_form", JSON.stringify(clean));
      const res = await api.post("/checkout", { plan_id: planId, ...clean });
      window.location.href = res.payment_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  if (plan === undefined) return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 md:py-16 animate-pulse" data-testid="checkout-loading">
      <div className="h-10 bg-bm-sand/60 rounded w-1/3 mb-10" />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-3 space-y-4">{[1,2,3,4].map((i) => <div key={i} className="h-14 bg-bm-sand/60 rounded-lg" />)}</div>
        <div className="md:col-span-2 h-56 bg-bm-sand/60 rounded-xl" />
      </div>
    </div>
  );
  if (plan === null) return <NotFound title="Plan not found" message="This subscription plan doesn't exist or is no longer available." testId="plan-not-found" />;
  const mag = plan.magazines?.[0];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 md:py-16" data-testid="checkout-page">
      <SEO title="Checkout" path={`/checkout/${planId}`} />
      <Link to={mag ? `/magazines/${mag.slug}` : "/pricing"} className="inline-flex items-center gap-1 text-sm text-bm-muted hover:text-bm-terra transition-colors duration-200" data-testid="checkout-back-link">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <span className="eyebrow block mt-6">Almost there</span>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 text-bm-ink">Checkout</h1>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10 mt-10">
        <form onSubmit={submit} className="md:col-span-3 space-y-4" data-testid="checkout-form" noValidate>
          <h2 className="font-display font-semibold text-2xl text-bm-ink">Subscriber details</h2>
          {[["name", "Full name", "text"], ["email", "Email address", "email"], ["phone", "Phone (for delivery & updates)", "tel"]].map(([k, label, type]) => (
            <div key={k}>
              <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-bm-muted mb-1.5">{label}</label>
              <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} onBlur={() => blur(k)}
                aria-invalid={!!errors[k]} data-testid={`checkout-${k}-input`} className={`bm-input ${errors[k] ? "!border-red-400" : ""}`} />
              <FieldError msg={errors[k]} id={`checkout-${k}`} />
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-bm-muted mb-1.5">Delivery address (print edition)</label>
            <textarea value={form.address} onChange={(e) => set("address", e.target.value)} onBlur={() => blur("address")} rows={2}
              aria-invalid={!!errors.address} data-testid="checkout-address-input" className={`bm-input ${errors.address ? "!border-red-400" : ""}`} />
            <FieldError msg={errors.address} id="checkout-address" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-bm-muted mb-1.5">{label}</label>
                <input value={form[k]} onChange={(e) => set(k, e.target.value)} onBlur={() => blur(k)}
                  aria-invalid={!!errors[k]} data-testid={`checkout-${k}-input`} className={`bm-input ${errors[k] ? "!border-red-400" : ""}`} />
                <FieldError msg={errors[k]} id={`checkout-${k}`} />
              </div>
            ))}
          </div>
          <button type="submit" disabled={loading} data-testid="pay-now-btn" className="btn-primary w-full !py-4 mt-4">
            {loading ? "Preparing secure checkout..." : `Pay ₹${plan.price.toLocaleString("en-IN")} Securely`}
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-bm-muted"><ShieldCheck className="w-4 h-4" /> UPI, cards, netbanking & wallets via Razorpay</p>
        </form>
        <aside className="md:col-span-2 bm-card p-6 h-fit" data-testid="order-summary">
          <h3 className="font-display font-semibold text-xl mb-4 text-bm-ink">Order summary</h3>
          <div className="flex gap-4">
            {mag && <img src={imgUrl(mag.cover_image)} alt={mag.title} className="w-16 aspect-[3/4] object-cover rounded-md shadow-cover" />}
            <div>
              <div className="font-semibold text-sm text-bm-ink">{plan.name}</div>
              <div className="text-xs text-bm-muted mt-1">{plan.duration_value} {plan.duration_unit} · Print + Digital</div>
            </div>
          </div>
          <div className="border-t border-bm-line mt-6 pt-4 space-y-2 text-sm">
            {plan.original_price > plan.price && (
              <div className="flex justify-between text-bm-muted"><span>MRP</span><span className="line-through">₹{plan.original_price.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between font-semibold text-base text-bm-ink"><span>Total</span><span data-testid="summary-total">₹{plan.price.toLocaleString("en-IN")}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function MockPayPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  useEffect(() => { api.get(`/orders/${orderId}/status`).then(setOrder).catch(() => {}); }, [orderId]);

  const pay = async () => {
    setProcessing(true);
    try {
      await api.post("/payments/mock-complete", { order_id: orderId });
      navigate(`/checkout/success?order_id=${orderId}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(false);
    }
  };

  const fail = async () => {
    setProcessing(true);
    try {
      await api.post("/payments/mock-fail", { order_id: orderId });
      navigate(`/checkout/failed?order_id=${orderId}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="mock-pay-page">
      <SEO title="Test Payment" path="/checkout/mock" />
      <div className="bm-card border-2 border-dashed !border-bm-sand-dark p-10">
        <span className="eyebrow">Razorpay Test Mode</span>
        <h1 className="font-display text-3xl font-bold mt-4 text-bm-ink">Mock Payment</h1>
        {order && <p className="text-sm text-bm-body mt-2">{order.plan_name} · ₹{order.amount.toLocaleString("en-IN")} · {order.order_number}</p>}
        <p className="text-xs text-bm-muted mt-4">Razorpay keys are not configured, so this simulates the hosted payment page.</p>
        <button onClick={pay} disabled={processing} data-testid="mock-pay-confirm-btn" className="btn-primary w-full mt-8">
          {processing ? "Processing..." : "Simulate Successful Payment"}
        </button>
        <button onClick={fail} disabled={processing} data-testid="mock-pay-fail-btn"
          className="w-full mt-3 text-sm font-semibold text-red-700 border border-red-300 rounded-lg px-6 py-3 hover:bg-red-50 transition-colors duration-200 disabled:opacity-50">
          Simulate Failed Payment
        </button>
      </div>
    </div>
  );
}

export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || "";
  const [order, setOrder] = useState(null);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (params.get("razorpay_payment_id")) {
        try {
          await api.post("/payments/razorpay/verify", Object.fromEntries(params.entries()));
        } catch { /* fallthrough to status poll */ }
      }
      // Poll a few times: webhook/callback can lag behind the redirect
      for (let i = 0; i < 5; i++) {
        try {
          const o = await api.get(`/orders/${orderId}/status`);
          if (cancelled) return;
          setOrder(o);
          if (o.status !== "pending") break;
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setVerifying(false);
    };
    if (orderId) run(); else setVerifying(false);
    return () => { cancelled = true; };
  }, [orderId]); // eslint-disable-line

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="checkout-success-page">
      <SEO title="Order Confirmation" path="/checkout/success" />
      {verifying ? (
        <div data-testid="verifying-payment">
          <div className="w-12 h-12 mx-auto border-4 border-bm-sand border-t-bm-terra rounded-full animate-spin" />
          <p className="text-bm-muted mt-6">Confirming your payment...</p>
        </div>
      ) : order ? (
        <>
          {order.status === "paid" ? (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-bm-terra" />
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-6 text-bm-ink" data-testid="success-heading">Subscription confirmed!</h1>
              <p className="text-bm-body mt-3 text-sm leading-relaxed">
                Order <span className="font-semibold">{order.order_number}</span> · {order.plan_name} — a confirmation has been sent to your email.
              </p>
              <div className="flex justify-center gap-4 mt-8 flex-wrap">
                {order.access_token && order.magazine_slug && (
                  <Link to={`/read/${order.magazine_slug}?key=${order.access_token}`} data-testid="read-full-issue-btn" className="btn-primary">
                    Read Full Issue Now
                  </Link>
                )}
                <Link to="/magazines" data-testid="continue-browsing-btn" className="btn-secondary">Continue Browsing</Link>
              </div>
            </>
          ) : order.status === "failed" ? (
            <>
              <XCircle className="w-16 h-16 mx-auto text-red-600" />
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-6 text-bm-ink" data-testid="failed-heading">Payment didn't go through</h1>
              <p className="text-bm-body mt-3 text-sm leading-relaxed" data-testid="failure-reason">
                {order.failure_reason || "The payment was not completed."} Order <span className="font-semibold">{order.order_number}</span> was saved - you have not been charged.
              </p>
              <div className="flex justify-center gap-4 mt-8 flex-wrap">
                <Link to={`/checkout/${order.plan_id}`} data-testid="try-again-btn" className="btn-primary">Try Again</Link>
                <Link to="/pricing" data-testid="choose-plan-btn" className="btn-secondary">Choose Another Plan</Link>
              </div>
            </>
          ) : (
            <>
              <Clock className="w-16 h-16 mx-auto text-bm-sand-dark" />
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-6 text-bm-ink" data-testid="pending-heading">Payment is being confirmed</h1>
              <p className="text-bm-body mt-3 text-sm leading-relaxed">
                Order <span className="font-semibold">{order.order_number}</span> is awaiting gateway confirmation. If you completed the payment, it will activate automatically within a few minutes - no need to pay again.
              </p>
              <div className="flex justify-center gap-4 mt-8 flex-wrap">
                <Link to="/track" data-testid="track-order-btn" className="btn-primary">Track My Order</Link>
                <Link to="/magazines" className="btn-secondary">Continue Browsing</Link>
              </div>
            </>
          )}
        </>
      ) : <p className="text-bm-muted" data-testid="order-not-found">Order not found.</p>}
    </div>
  );
}

export function CheckoutFailedPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || "";
  const [order, setOrder] = useState(null);
  useEffect(() => { if (orderId) api.get(`/orders/${orderId}/status`).then(setOrder).catch(() => {}); }, [orderId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="checkout-failed-page">
      <SEO title="Payment Failed" path="/checkout/failed" />
      <XCircle className="w-16 h-16 mx-auto text-red-600" />
      <h1 className="font-display text-3xl md:text-4xl font-bold mt-6 text-bm-ink" data-testid="failed-heading">Payment didn't go through</h1>
      <p className="text-bm-body mt-3 text-sm leading-relaxed" data-testid="failure-reason">
        {order?.failure_reason || "The payment was declined or timed out at the gateway."}
        {order && <> Order <span className="font-semibold">{order.order_number}</span> was saved as <span className="font-semibold">{order.status}</span> — you have not been charged.</>}
      </p>
      <div className="flex justify-center gap-4 mt-8 flex-wrap">
        {order?.plan_id && (
          <Link to={`/checkout/${order.plan_id}`} data-testid="try-again-btn" className="btn-primary">
            Try Again — your details are saved
          </Link>
        )}
        <Link to="/pricing" data-testid="choose-plan-btn" className="btn-secondary">Choose Another Plan</Link>
      </div>
    </div>
  );
}
